/**
 * Subscription checkout.
 *
 * The product, kilogram, plan and delivery frequency were all chosen on the Shopify
 * product page and are frozen inside the checkout session. This page therefore renders
 * them read-only and offers exactly one way to change them: go back to the product page.
 *
 * Card data never touches this page. Submitting hands off to the iyzico hosted Checkout
 * Form, and nothing financial happens until the customer presses
 * "Aboneliği Başlat ve Öde".
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';

import {
  TURKEY_PROVINCES,
  canonicalDistrict,
  canonicalProvince,
  districtsOf,
} from '../data/turkey-regions';
import { useAuth } from '../features/auth/AuthContext';
import { api, getErrorMessage } from '../lib/api';
import { env } from '../lib/env';
import {
  PHONE_NATIONAL_LENGTH,
  formatNationalDigits,
  nationalPhoneError,
  toE164,
  toNationalDigits,
} from '../lib/phone';
import type { AddressChoice, Branding, CheckoutView } from '../lib/schemas';
import { addressSchema, newSubscriptionInputSchema } from '../lib/schemas';
import '../styles/checkout.css';

type AddressForm = {
  firstName: string;
  lastName: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  district: string;
  province: string;
  postalCode: string;
};

const EMPTY_ADDRESS: AddressForm = {
  firstName: '',
  lastName: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  district: '',
  province: '',
  postalCode: '',
};

const NEW_ADDRESS_ID = '__new__';

function brandingStyle(branding: Branding): Record<string, string> {
  return {
    '--tk-color-primary': branding.primaryColor,
    '--tk-color-primary-hover': branding.primaryHoverColor,
    '--tk-color-primary-text': branding.primaryTextColor,
    '--tk-color-background': branding.backgroundColor,
    '--tk-color-surface': branding.surfaceColor,
    '--tk-color-surface-muted': branding.surfaceMutedColor,
    '--tk-color-text': branding.textColor,
    '--tk-color-text-muted': branding.mutedTextColor,
    '--tk-color-heading': branding.headingColor,
    '--tk-color-border': branding.borderColor,
    '--tk-color-success': branding.successColor,
    '--tk-color-critical': branding.criticalColor,
    '--tk-font-body': branding.bodyFontFamily,
    '--tk-font-heading': branding.headingFontFamily,
    '--tk-radius-button': branding.buttonRadius,
    '--tk-radius-card': branding.cardRadius,
    '--tk-radius-input': branding.inputRadius,
    '--tk-radius-badge': branding.badgeRadius,
    '--tk-container-width': branding.containerWidth,
  };
}

const PROVINCE_NAMES: readonly string[] = TURKEY_PROVINCES.map((province) => province.name);

function toForm(choice: AddressChoice | undefined): AddressForm {
  const address = choice?.address;
  if (!address) return EMPTY_ADDRESS;
  // A stored address was typed freely, or came from Shopify where the district lives in
  // `city`. Both are mapped onto the canonical list entries so the two selects can show
  // them; anything the list does not recognise stays empty and is chosen again, which is
  // safer than shipping to a spelling the carrier cannot resolve.
  const province = canonicalProvince(address.province ?? '') ?? '';
  const district =
    canonicalDistrict(province, address.district ?? '') ??
    canonicalDistrict(province, address.city ?? '') ??
    '';
  return {
    firstName: address.firstName ?? '',
    lastName: address.lastName ?? '',
    phone: toNationalDigits(address.phone ?? ''),
    address1: address.address1 ?? '',
    address2: address.address2 ?? '',
    city: district,
    district,
    province,
    postalCode: address.postalCode ?? '',
  };
}

export function CheckoutPage() {
  const { token = '' } = useParams();
  // The page serves guests and signed-in customers from the same route. Loading the
  // session before Firebase has restored an existing session would send the request
  // without its token, and a session already bound to that customer would come back as
  // "not found" — so the fetch waits for the auth state to settle, however it settles.
  const { loading: authLoading } = useAuth();
  const [view, setView] = useState<CheckoutView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [addressChoiceId, setAddressChoiceId] = useState<string>(NEW_ADDRESS_ID);
  const [form, setForm] = useState<AddressForm>(EMPTY_ADDRESS);
  const [identityNumber, setIdentityNumber] = useState('');
  /** Guest checkout: the address the account is created under. */
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [saveToShopify, setSaveToShopify] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Guards against a double click racing the redirect. The server-side single-use session
  // is the real defence; this only avoids a confusing second spinner.
  const submittedRef = useRef(false);
  // A closed <details> hides its content at every width, so the order summary must be
  // forced open on desktop where the toggle bar itself is hidden.
  const [desktop, setDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1000px)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia('(min-width: 1000px)');
    const onChange = (event: MediaQueryListEvent) => setDesktop(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    const controller = new AbortController();
    api
      .getCheckoutSession(token, controller.signal)
      .then((result) => {
        setView(result);
        setEmail(result.contact.email ?? '');
        const selected = result.addresses.choices.find(
          (choice) => choice.id === result.addresses.selectedId,
        );
        setAddressChoiceId(selected?.id ?? NEW_ADDRESS_ID);
        setForm(toForm(selected));
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(getErrorMessage(error));
      });
    return () => controller.abort();
  }, [token, authLoading]);

  const choices = view?.addresses.choices ?? [];
  const usableChoices = useMemo(
    () => choices.filter((choice) => choice.address !== undefined),
    [choices],
  );

  const selectChoice = useCallback(
    (id: string) => {
      setAddressChoiceId(id);
      if (id === NEW_ADDRESS_ID) {
        setForm(EMPTY_ADDRESS);
        return;
      }
      setForm(toForm(choices.find((choice) => choice.id === id)));
    },
    [choices],
  );

  function updateField(field: keyof AddressForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  const districtOptions = useMemo(() => districtsOf(form.province), [form.province]);

  /** Changing the province invalidates the district beneath it, so it is cleared. */
  function selectProvince(_field: keyof AddressForm, value: string) {
    setForm((current) => ({ ...current, province: value, district: '', city: '' }));
  }

  /**
   * `city` is not a field the customer fills in. Turkish Shopify addresses — and the DHL
   * shipment the order turns into — read the district out of `city`, so the two are kept
   * identical instead of asking for the same thing twice under two labels.
   */
  function selectDistrict(_field: keyof AddressForm, value: string) {
    setForm((current) => ({ ...current, district: value, city: value }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submittedRef.current || view === null) return;
    setSubmitError(null);

    const candidateAddress = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: toE164(form.phone),
      address1: form.address1.trim(),
      ...(form.address2.trim() === '' ? {} : { address2: form.address2.trim() }),
      city: form.city.trim(),
      district: form.district.trim(),
      province: form.province.trim(),
      ...(form.postalCode.trim() === '' ? {} : { postalCode: form.postalCode.trim() }),
      countryCode: 'TR' as const,
    };

    const parsedAddress = addressSchema.safeParse(candidateAddress);
    const errors: Record<string, string> = {};
    // The account is created from this address, so an empty one cannot be waved through
    // by the optional field in the shared schema.
    if (view.guest === true && email.trim() === '') errors.email = 'E-posta adresinizi girin.';
    if (!parsedAddress.success) {
      for (const issue of parsedAddress.error.issues) {
        const key = String(issue.path[0]);
        errors[key] ??= issue.message;
      }
    }
    const parsedInput = newSubscriptionInputSchema.safeParse({
      checkoutSessionToken: token,
      // Only a guest supplies one: a signed-in customer's address comes from their account
      // and must not be changeable from the page.
      ...(view.guest === true ? { email: email.trim() } : {}),
      identityNumber,
      shippingAddress: candidateAddress,
      consentAccepted: consent,
      termsVersion: view.legal.termsVersion ?? '',
      privacyVersion: view.legal.privacyVersion ?? '',
      distanceSalesVersion: view.legal.distanceSalesVersion ?? '',
      saveAddressToShopify: saveToShopify,
    });
    if (!parsedInput.success) {
      for (const issue of parsedInput.error.issues) {
        const path = issue.path;
        const key = path[0] === 'shippingAddress' ? String(path[1]) : String(path[0]);
        errors[key] ??= issue.message;
      }
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0 || !parsedInput.success) {
      // The submit button sits at the bottom; a validation error above the fold would
      // otherwise be invisible. Walk the customer to the first offending field.
      const firstKey = Object.keys(errors)[0];
      const targetId =
        firstKey === 'email'
          ? 'tk-email'
          : firstKey === 'identityNumber'
            ? 'tk-identity'
            : firstKey === 'consentAccepted'
              ? 'tk-consent'
              : `tk-${firstKey ?? ''}`;
      requestAnimationFrame(() => {
        const element =
          document.getElementById(targetId) ?? document.querySelector('[aria-invalid="true"]');
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (element instanceof HTMLElement) element.focus({ preventScroll: true });
      });
      return;
    }

    submittedRef.current = true;
    setSubmitting(true);
    try {
      const result = await api.initializeSubscription(parsedInput.data);
      // Hand off to the iyzico hosted form. Card fields only ever exist there.
      window.location.assign(result.checkoutUrl);
    } catch (error) {
      submittedRef.current = false;
      setSubmitting(false);
      setSubmitError(getErrorMessage(error));
    }
  }

  if (loadError !== null) {
    return (
      <main className="tk-checkout">
        <div className="tk-checkout__frame">
          <div className="tk-card" style={{ paddingBlockStart: '2rem' }}>
            <h1 className="tk-card__title">Ödeme oturumu açılamadı</h1>
            <p className="tk-alert">{loadError}</p>
            <p className="tk-card__note">
              Abonelik seçiminiz zaman aşımına uğramış olabilir. Ürün sayfasına dönüp “Abone Ol”
              butonuna tekrar basın.
            </p>
            {/* Telling someone to go back somewhere is not the same as taking them there. */}
            <a
              className="tk-button"
              href={`${env.shopifyStorefrontUrl}${env.subscriptionProductPath}`}
              style={{ marginBlockStart: '1rem' }}
            >
              Ürün sayfasına dön
            </a>
          </div>
        </div>
      </main>
    );
  }

  if (view === null) {
    return (
      <main className="tk-checkout">
        <div className="tk-checkout__frame">
          <p role="status" style={{ paddingBlockStart: '2rem' }}>
            Ödeme sayfası hazırlanıyor…
          </p>
        </div>
      </main>
    );
  }

  const { offer, branding, summaryLines, contact, addresses } = view;

  const summary = (
    <div className="tk-summary-disclosure__body">
      <div className="tk-summary__product">
        {offer.imageUrl ? (
          <img className="tk-summary__image" src={offer.imageUrl} alt="" width={76} height={76} />
        ) : (
          <div className="tk-summary__image tk-summary__image--placeholder" aria-hidden="true">
            Görsel yok
          </div>
        )}
        <div className="tk-summary__titles">
          {offer.badgeText ? <span className="tk-badge">{offer.badgeText}</span> : null}
          <h2 className="tk-summary__product-title">{offer.productTitle}</h2>
          <span className="tk-summary__variant">{offer.variantTitle}</span>
        </div>
      </div>

      <dl className="tk-summary__lines">
        {summaryLines.map((line) => (
          <div
            className={[
              'tk-summary__line',
              line.emphasis === 'STRIKETHROUGH' ? 'tk-summary__line--strikethrough' : '',
              line.emphasis === 'SAVING' ? 'tk-summary__line--saving' : '',
              line.emphasis === 'TOTAL' ? 'tk-summary__line--total' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            key={`${line.label}-${line.value}`}
          >
            <dt>{line.label}</dt>
            <dd className="tk-summary__value">{line.value}</dd>
          </div>
        ))}
      </dl>

      <p className="tk-summary__locked">
        <span>Seçimleriniz bu sayfada kilitlidir.</span>
        <a className="tk-summary__change" href={view.changeSelectionUrl}>
          Ürün sayfasına dön
        </a>
      </p>
    </div>
  );

  return (
    <main className="tk-checkout" style={brandingStyle(branding) as React.CSSProperties}>
      <div className="tk-checkout__frame">
        <header className="tk-checkout__header">
          <img className="tk-checkout__logo" src={branding.logoUrl} alt={branding.logoAlt} />
          <span className="tk-checkout__secure">🔒 Güvenli abonelik ödemesi — iyzico</span>
        </header>
        <div className="tk-checkout__shell">
          <div className="tk-checkout__main">
            <div className="tk-checkout__main-inner">
              <form className="tk-checkout__grid" onSubmit={handleSubmit} noValidate>
                <div className="tk-checkout__column">
                  <section className="tk-card">
                    <h2 className="tk-card__title">İletişim</h2>
                    <div className="tk-grid-2">
                      <div className="tk-field">
                        <input
                          className="tk-field__input"
                          id="tk-email"
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          value={view.guest === true ? email : (contact.email ?? '')}
                          readOnly={!view.guest}
                          placeholder=" "
                          aria-invalid={fieldErrors.email ? 'true' : undefined}
                          aria-describedby="tk-email-note"
                          onChange={(event) => setEmail(event.target.value)}
                        />
                        <label className="tk-field__label" htmlFor="tk-email">
                          E-posta
                        </label>
                        {fieldErrors.email ? (
                          <span className="tk-field__error">{fieldErrors.email}</span>
                        ) : null}
                      </div>
                      <div className="tk-field">
                        <input
                          className="tk-field__input"
                          id="tk-identity"
                          inputMode="numeric"
                          autoComplete="off"
                          maxLength={11}
                          value={identityNumber}
                          placeholder=" "
                          aria-invalid={fieldErrors.identityNumber ? 'true' : undefined}
                          onChange={(event) =>
                            setIdentityNumber(event.target.value.replace(/\D/g, '').slice(0, 11))
                          }
                        />
                        <label className="tk-field__label" htmlFor="tk-identity">
                          T.C. Kimlik Numarası
                        </label>
                        {fieldErrors.identityNumber ? (
                          <span className="tk-field__error">{fieldErrors.identityNumber}</span>
                        ) : null}
                      </div>
                    </div>
                    <p className="tk-card__note" id="tk-email-note">
                      {view.guest
                        ? 'Aboneliğiniz bu adrese tanımlanır; hesabınızı sizin için oluştururuz. Aboneliğinizi daha sonra bu e-posta ile Aboneliklerim sayfasından yönetebilirsiniz.'
                        : 'E-posta adresiniz Tropikal Akdeniz hesabınızdan alınır. Değiştirmek için hesap bilgilerinizi güncelleyin.'}
                    </p>
                  </section>

                  <section className="tk-card">
                    <h2 className="tk-card__title">Teslimat adresi</h2>

                    {addresses.shopifyAddressAccessDenied ? (
                      <p className="tk-alert tk-alert--info">
                        Teslimat adresinizi bu sayfada girin.
                      </p>
                    ) : null}

                    {addresses.showPicker || usableChoices.length > 0 ? (
                      <div
                        className="tk-address-choices"
                        role="radiogroup"
                        aria-label="Kayıtlı adresler"
                      >
                        {usableChoices.map((choice) => (
                          <label className="tk-address-choice" key={choice.id}>
                            <input
                              type="radio"
                              name="tk-address"
                              value={choice.id}
                              checked={addressChoiceId === choice.id}
                              onChange={() => selectChoice(choice.id)}
                            />
                            <span className="tk-address-choice__label">
                              <span>{choice.label}</span>
                              {choice.isDefault ? (
                                <span className="tk-address-choice__hint">
                                  Varsayılan adresiniz
                                </span>
                              ) : null}
                            </span>
                          </label>
                        ))}
                        <label className="tk-address-choice">
                          <input
                            type="radio"
                            name="tk-address"
                            value={NEW_ADDRESS_ID}
                            checked={addressChoiceId === NEW_ADDRESS_ID}
                            onChange={() => selectChoice(NEW_ADDRESS_ID)}
                          />
                          <span className="tk-address-choice__label">
                            <span>Yeni adres gir</span>
                            <span className="tk-address-choice__hint">
                              Bu abonelik için farklı bir adres kullanın
                            </span>
                          </span>
                        </label>
                      </div>
                    ) : null}

                    <div className="tk-grid-2">
                      <Field
                        id="firstName"
                        label="Ad"
                        value={form.firstName}
                        error={fieldErrors.firstName}
                        onChange={updateField}
                        autoComplete="given-name"
                      />
                      <Field
                        id="lastName"
                        label="Soyad"
                        value={form.lastName}
                        error={fieldErrors.lastName}
                        onChange={updateField}
                        autoComplete="family-name"
                      />
                      <PhoneField
                        value={form.phone}
                        error={fieldErrors.phone}
                        onChange={(digits) => updateField('phone', digits)}
                      />
                      <Field
                        id="postalCode"
                        label="Posta kodu"
                        value={form.postalCode}
                        error={fieldErrors.postalCode}
                        onChange={updateField}
                        autoComplete="postal-code"
                      />
                    </div>
                    <div className="tk-grid-2" style={{ marginBlockStart: '1rem' }}>
                      <Field
                        id="address1"
                        label="Açık adres"
                        value={form.address1}
                        error={fieldErrors.address1}
                        onChange={updateField}
                        autoComplete="address-line1"
                        wide
                      />
                      <Field
                        id="address2"
                        label="Adres devamı (isteğe bağlı)"
                        value={form.address2}
                        error={fieldErrors.address2}
                        onChange={updateField}
                        autoComplete="address-line2"
                        wide
                      />
                      <SelectField
                        id="province"
                        label="İl/Şehir"
                        value={form.province}
                        error={fieldErrors.province}
                        placeholder="Seçim yapınız"
                        options={PROVINCE_NAMES}
                        autoComplete="address-level1"
                        onChange={selectProvince}
                      />
                      <SelectField
                        id="district"
                        label="İlçe"
                        value={form.district}
                        error={fieldErrors.district ?? fieldErrors.city}
                        placeholder={form.province === '' ? 'Önce il seçiniz' : 'Seçim yapınız'}
                        options={districtOptions}
                        disabled={form.province === ''}
                        autoComplete="address-level2"
                        onChange={selectDistrict}
                      />
                    </div>

                    {view.guest === true ? null : (
                      <label className="tk-checkbox" style={{ marginBlockStart: '1rem' }}>
                        <input
                          type="checkbox"
                          checked={saveToShopify}
                          onChange={(event) => setSaveToShopify(event.target.checked)}
                        />
                        <span>Bu adresi Tropikal Akdeniz hesabıma da kaydet</span>
                      </label>
                    )}
                  </section>

                  <section className="tk-card">
                    <h2 className="tk-card__title">Abonelik onayı</h2>
                    <label className="tk-checkbox">
                      <input
                        id="tk-consent"
                        type="checkbox"
                        checked={consent}
                        aria-invalid={fieldErrors.consentAccepted ? 'true' : undefined}
                        onChange={(event) => setConsent(event.target.checked)}
                      />
                      <span>
                        <a href="/legal/subscription-terms" target="_blank" rel="noreferrer">
                          Abonelik koşullarını
                        </a>
                        ,{' '}
                        <a href="/legal/distance-sales" target="_blank" rel="noreferrer">
                          ön bilgilendirme ve mesafeli satış sözleşmesini
                        </a>{' '}
                        ve{' '}
                        <a href="/legal/privacy" target="_blank" rel="noreferrer">
                          gizlilik politikasını
                        </a>{' '}
                        okudum, onaylıyorum. {offer.frequencyLabel} olarak{' '}
                        {offer.subscriptionPriceLabel} tahsil edileceğini kabul ediyorum.
                      </span>
                    </label>
                    {fieldErrors.consentAccepted ? (
                      <span className="tk-field__error">{fieldErrors.consentAccepted}</span>
                    ) : null}

                    <p className="tk-card__note">
                      Kart bilgileriniz yalnızca iyzico’nun güvenli ödeme formunda girilir; bu sayfa
                      kart numarası, CVV veya son kullanma tarihi istemez ve saklamaz.
                    </p>

                    {submitError ? (
                      <p className="tk-alert" role="alert" style={{ marginBlockStart: '1rem' }}>
                        {submitError}
                      </p>
                    ) : null}

                    <button
                      className="tk-button"
                      type="submit"
                      disabled={submitting}
                      style={{ marginBlockStart: '1rem' }}
                    >
                      {submitting ? 'Güvenli ödemeye yönlendiriliyor…' : 'Aboneliği Başlat ve Öde'}
                    </button>
                  </section>
                </div>
              </form>
            </div>
          </div>

          <aside className="tk-checkout__column--summary">
            <div className="tk-checkout__summary-inner">
              <details className="tk-summary-disclosure" open={desktop || undefined}>
                <summary>
                  <span>Sipariş özeti</span>
                  <strong>{offer.subscriptionPriceLabel}</strong>
                </summary>
                {summary}
              </details>
            </div>
          </aside>
        </div>

        <footer className="tk-checkout__footer">
          <a href="/legal/subscription-terms">Abonelik koşulları</a>
          <a href="/legal/distance-sales">Mesafeli satış sözleşmesi</a>
          <a href="/legal/privacy">Gizlilik politikası</a>
        </footer>
      </div>
      <a
        aria-label="WhatsApp canlı destek"
        className="tk-whatsapp"
        href="https://wa.me/905455334331"
        rel="noopener noreferrer"
        target="_blank"
      >
        <svg aria-hidden="true" viewBox="0 0 32 32">
          <path d="M16 3C9.4 3 4 8.3 4 14.8c0 2.6.9 5 2.3 7L4 29l7.4-2.3c1.4.7 3 1.1 4.6 1.1 6.6 0 12-5.3 12-11.9S22.6 3 16 3zm0 21.6c-1.5 0-2.9-.4-4.2-1.1l-.3-.2-4.4 1.4 1.4-4.2-.2-.3c-1.2-1.6-1.9-3.5-1.9-5.4 0-5.3 4.3-9.7 9.6-9.7s9.6 4.3 9.6 9.7-4.3 9.8-9.6 9.8zm5.3-7.3c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.8-1.5-1.7-1.6-2-.2-.3 0-.4.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.7-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.4z" />
        </svg>
        Canlı Destek
      </a>
    </main>
  );
}

function Field(props: {
  id: keyof AddressForm;
  label: string;
  value: string;
  error?: string | undefined;
  autoComplete?: string | undefined;
  /** Spans the whole row instead of one grid column. */
  wide?: boolean;
  onChange: (field: keyof AddressForm, value: string) => void;
}) {
  const inputId = `tk-${props.id}`;
  // The input renders before its floating label so the CSS sibling selector can shrink
  // the label whenever the field has a value; placeholder=" " keeps :placeholder-shown
  // meaningful without showing any placeholder text.
  return (
    <div className={props.wide ? 'tk-field tk-field--wide' : 'tk-field'}>
      <input
        className="tk-field__input"
        id={inputId}
        value={props.value}
        placeholder=" "
        aria-invalid={props.error ? 'true' : undefined}
        {...(props.autoComplete ? { autoComplete: props.autoComplete } : {})}
        onChange={(event) => props.onChange(props.id, event.target.value)}
      />
      <label className="tk-field__label" htmlFor={inputId}>
        {props.label}
      </label>
      {props.error ? <span className="tk-field__error">{props.error}</span> : null}
    </div>
  );
}

/**
 * Turkish mobile number, entered as ten digits behind a fixed +90.
 *
 * The delivery number ends up at the carrier, which only accepts a +90 mobile; leaving it
 * free text meant that verdict arrived after the order was placed. Anything that is not a
 * digit is dropped as it is typed, the field stops at ten, and the reason a number is not
 * acceptable is shown while it is being typed rather than at submit time.
 */
function PhoneField(props: {
  value: string;
  error?: string | undefined;
  onChange: (digits: string) => void;
}) {
  const liveError = nationalPhoneError(props.value);
  const message = props.error ?? liveError;
  return (
    <div className="tk-field tk-field--filled tk-field--phone">
      <span className="tk-field__prefix" aria-hidden="true">
        +90
      </span>
      <input
        className="tk-field__input"
        id="tk-phone"
        type="tel"
        inputMode="numeric"
        maxLength={PHONE_NATIONAL_LENGTH + 3}
        value={formatNationalDigits(props.value)}
        placeholder="532 123 45 67"
        aria-invalid={message ? 'true' : undefined}
        aria-describedby={message ? 'tk-phone-error' : undefined}
        autoComplete="tel-national"
        onChange={(event) => props.onChange(toNationalDigits(event.target.value))}
      />
      <label className="tk-field__label" htmlFor="tk-phone">
        Telefon
      </label>
      {message ? (
        <span className="tk-field__error" id="tk-phone-error">
          {message}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A select with the same floating label as the text fields. The label is always in its
 * shrunk position — a closed select shows its own value, so there is nothing for the
 * label to sit on top of — and the empty option carries the instruction ("Seçim yapınız")
 * so the field explains itself without an extra line of helper text.
 */
function SelectField(props: {
  id: keyof AddressForm;
  label: string;
  value: string;
  placeholder: string;
  options: readonly string[];
  error?: string | undefined;
  disabled?: boolean;
  autoComplete?: string | undefined;
  onChange: (field: keyof AddressForm, value: string) => void;
}) {
  const inputId = `tk-${props.id}`;
  return (
    <div className="tk-field tk-field--filled">
      <select
        className="tk-field__select"
        id={inputId}
        value={props.value}
        aria-invalid={props.error ? 'true' : undefined}
        {...(props.disabled ? { disabled: true } : {})}
        {...(props.autoComplete ? { autoComplete: props.autoComplete } : {})}
        onChange={(event) => props.onChange(props.id, event.target.value)}
      >
        <option value="">{props.placeholder}</option>
        {props.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <label className="tk-field__label" htmlFor={inputId}>
        {props.label}
      </label>
      {props.error ? <span className="tk-field__error">{props.error}</span> : null}
    </div>
  );
}
